import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Filter } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Product } from '../data/products';
import ProductCard from '../components/ProductCard';
import { fetchProductsBySubcategory, fetchAllProducts, ApiProduct } from '../services/api';
import { useCategories } from '../hooks/useCategories';
import { useCategoryById } from '../hooks/useCategoryById';
import './ProductsPage.css';

const ProductsPage: React.FC = () => {
    const { category } = useParams<{ category?: string }>();
    const [searchParams] = useSearchParams();
    const subcategory = searchParams.get('subcategory');
    const navigate = useNavigate();
    const { translations, isRTL } = useLanguage();
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(category ? parseInt(category) : null);
    const { categories } = useCategories();
    const categoryId = selectedCategoryId;
    const { category: categoryWithSubs } = useCategoryById(categoryId);

    // Transform API product to local Product format
    const transformApiProduct = (apiProduct: ApiProduct): Product => {
        return {
            id: apiProduct.id,
            title: apiProduct.name.en,
            titleAr: apiProduct.name.ar,
            description: apiProduct.description.en,
            descriptionAr: apiProduct.description.ar,
            category: apiProduct.category,
            subcategory: apiProduct.subcategory,
            image: apiProduct.images[0] || '',
            images: apiProduct.images,
            price: apiProduct.price,
        };
    };

    // Sync selectedCategoryId with URL params
    useEffect(() => {
        setSelectedCategoryId(category ? parseInt(category) : null);
    }, [category]);

    useEffect(() => {
        const loadProducts = async () => {
            setIsLoading(true);
            
            try {
                // If "All" is selected (no category and no subcategory), fetch all products
                if (!category && !subcategory) {
                    const apiProducts = await fetchAllProducts();
                    const transformedProducts = apiProducts
                        .filter(p => p.isActive)
                        .map(transformApiProduct);
                    setFilteredProducts(transformedProducts);
                }
                // If subcategory is selected, fetch products by subcategory
                else if (subcategory) {
                    const subcategoryId = parseInt(subcategory);
                    if (!isNaN(subcategoryId)) {
                        const apiProducts = await fetchProductsBySubcategory(subcategoryId);
                        const transformedProducts = apiProducts
                            .filter(p => p.isActive)
                            .map(transformApiProduct);
                        setFilteredProducts(transformedProducts);
                    } else {
                        setFilteredProducts([]);
                    }
                }
                // Category selected but no subcategory - don't load products yet
                else {
                    setFilteredProducts([]);
                }
            } catch (error) {
                console.error('Error loading products:', error);
                setFilteredProducts([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadProducts();
    }, [category, subcategory]);

    const getCategoryTitle = () => {
        if (!selectedCategoryId) return translations.products;
        if (categoryWithSubs) {
            return isRTL ? categoryWithSubs.name.ar : categoryWithSubs.name.en;
        }
        return translations.products;
    };

    const getSubcategoryTitle = () => {
        if (!subcategory) return '';
        if (categoryWithSubs) {
            const subcategoryInfo = categoryWithSubs.subcategories?.find(sub => sub.id === parseInt(subcategory));
            return subcategoryInfo ? (isRTL ? subcategoryInfo.name.ar : subcategoryInfo.name.en) : '';
        }
        return '';
    };

    const handleCategoryClick = (catId: number) => {
        setSelectedCategoryId(catId);
        navigate(`/products/${catId}`);
    };

    const handleAllClick = () => {
        setSelectedCategoryId(null);
        navigate('/products');
    };

    const handleSubcategoryClick = (subId: number) => {
        if (selectedCategoryId) {
            navigate(`/products/${selectedCategoryId}?subcategory=${subId}`);
        }
    };

    if (isLoading) {
        return (
            <div className="products-page">
                <div className="container">
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <p>{translations.loading || 'Loading products...'}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`products-page ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="container">
                {/* Breadcrumb */}
                <nav className="breadcrumb-compact">
                    <Link to="/" className="breadcrumb-link">{translations.home}</Link>
                    <span className="breadcrumb-separator">/</span>
                    {subcategory ? (
                        <>
                            <span className="breadcrumb-link" onClick={() => handleAllClick()} style={{ cursor: 'pointer' }}>
                                {getCategoryTitle()}
                            </span>
                            <span className="breadcrumb-separator">/</span>
                            <span className="breadcrumb-current">{getSubcategoryTitle()}</span>
                        </>
                    ) : (
                        <span className="breadcrumb-current">{getCategoryTitle()}</span>
                    )}
                </nav>

                {/* Page Title */}
                <div className="page-header-section">
                    <h1 className="page-title-main">
                        {subcategory ? getSubcategoryTitle() : getCategoryTitle()}
                    </h1>
                    <Link to="/" className="back-link-compact">
                        <ArrowLeft size={16} className={isRTL ? 'flip' : ''} />
                        {translations.home}
                    </Link>
                </div>

                {/* Category and Subcategory Bubbles - Side by Side */}
                <div className="filters-wrapper">
                    {/* Category Bubbles */}
                    <div className="category-bubbles-section">
                        <div className="section-label">
                            <Filter size={18} />
                            <span>{isRTL ? 'الفئات' : 'Categories'}</span>
                        </div>
                        <div className="category-bubbles">
                            <button
                                onClick={handleAllClick}
                                className={`category-bubble ${!selectedCategoryId ? 'active' : ''}`}
                            >
                                {isRTL ? 'الكل' : 'All'}
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => handleCategoryClick(cat.id)}
                                    className={`category-bubble ${selectedCategoryId === cat.id ? 'active' : ''}`}
                                >
                                    {isRTL ? cat.name.ar : cat.name.en}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Subcategory Bubbles - Beside categories */}
                    {selectedCategoryId && categoryWithSubs && categoryWithSubs.subcategories && categoryWithSubs.subcategories.length > 0 && (
                        <div className="subcategory-bubbles-section">
                            <div className="section-label">
                                <Filter size={18} />
                                <span>{isRTL ? 'الفئات الفرعية' : 'Subcategories'}</span>
                            </div>
                            <div className="subcategory-bubbles">
                                {categoryWithSubs.subcategories.map(sub => (
                                    <button
                                        key={sub.id}
                                        onClick={() => handleSubcategoryClick(sub.id)}
                                        className={`subcategory-bubble ${subcategory === String(sub.id) ? 'active' : ''}`}
                                    >
                                        {isRTL ? sub.name.ar : sub.name.en}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Products Grid */}
                <div className="products-section">
                    {filteredProducts.length > 0 ? (
                        <div className="products-grid">
                            {filteredProducts.map(product => {
                                console.log('Rendering product:', product.id, product.title, product.titleAr);
                                return <ProductCard key={product.id} product={product} />;
                            })}
                        </div>
                    ) : (
                        <div className="no-products">
                            <h3>
                                {selectedCategoryId && !subcategory
                                    ? (isRTL ? 'الرجاء اختيار فئة فرعية' : 'Please Select a Subcategory')
                                    : !selectedCategoryId
                                    ? (isRTL ? 'الرجاء اختيار فئة' : 'Please Select a Category')
                                    : (isRTL ? 'لا توجد منتجات متاحة' : 'No products available')
                                }
                            </h3>
                            <p>
                                {selectedCategoryId && !subcategory
                                    ? (isRTL 
                                        ? 'يرجى اختيار فئة فرعية من القائمة أعلاه لعرض المنتجات المتاحة.'
                                        : 'Please select a subcategory from the list above to view available products.')
                                    : !selectedCategoryId
                                    ? (isRTL
                                        ? 'يرجى اختيار فئة من القائمة أعلاه للبدء.'
                                        : 'Please select a category from the list above to get started.')
                                    : (isRTL
                                        ? 'عذراً، لا توجد منتجات في هذا التصنيف حالياً.'
                                        : 'Sorry, there are no products available in this category at the moment.')
                                }
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;